import {createApi} from '@reduxjs/toolkit/query/react';
import {AxiosRequestHeaders} from 'axios';

import httpBaseQuery, {IHttpBaseQueryMeta} from '../../../shared/api/rtk-query/httpBaseQuery';
import {ApiUrls} from '../../../shared/config/model/urls';
import IPortMonitor, {IPortMonitorInput, IPortMonitorListItem} from '../model/PortMonitor/IPortMonitor';
import ISummaryPortMonitors from '../model/PortMonitor/ISummaryPortMonitors';
import IHydraResponse from '../../../shared/api/model/HydraResponse';
import IPagination from '../../../shared/api/model/IPagination';
import ICollectionParams from '../../../shared/api/model/ICollectionParams';
import IOrderBy from '../../../shared/model/IOrderBy';
import {paginationAdapter} from '../../../shared/api/paginationHelper';
import {providesListUuid} from '../../../shared/api/rtk-query/RTKQueryHelper';
import {buildQuery} from '../../../shared/api/query';
import IApiViolation from '../../../shared/api/model/IApiViolation';

export interface IPortMonitorMutationError {
    message: unknown,
    violations?: IApiViolation[],
}

const withViolations = (
    baseQueryReturnValue: unknown,
    meta: IHttpBaseQueryMeta | undefined,
): IPortMonitorMutationError => ({
    message: baseQueryReturnValue,
    violations: meta?.violations,
});

export const portMonitorAPI = createApi({
    reducerPath: 'portMonitorAPI',
    baseQuery: httpBaseQuery(),
    tagTypes: ['PortMonitor', 'SummaryPortMonitors'],
    refetchOnMountOrArgChange: 60,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        addPortMonitor: build.mutation<IPortMonitor, IPortMonitorInput>({
            query: (data) => ({
                path: ApiUrls.PORT_MONITORS(),
                method: 'POST',
                data,
            }),
            transformErrorResponse: withViolations,
            invalidatesTags: [{ type: 'PortMonitor', id: 'LIST' }, 'SummaryPortMonitors'],
        }),
        getPortMonitors: build.query<IPagination<IPortMonitorListItem>, ICollectionParams>({
            query: ({page, sort, filters, limit}) => ({
                path: buildQuery(ApiUrls.PORT_MONITORS(page, sort, filters, limit)),
                headers: {Accept: 'application/ld+json'} as unknown as AxiosRequestHeaders,
            }),
            providesTags: (result) => providesListUuid(result?.items, 'PortMonitor'),
            transformResponse(
                rawResult: IHydraResponse<IPortMonitorListItem>,
            ): Promise<IPagination<IPortMonitorListItem>> | IPagination<IPortMonitorListItem> {
                return paginationAdapter(rawResult);
            },
        }),
        getPortMonitor: build.query<IPortMonitor, string>({
            query: (uuid) => ({
                path: ApiUrls.PORT_MONITOR(uuid),
            }),
            providesTags: (result, error, uuid) => [{ type: 'PortMonitor', id: uuid }],
        }),
        updatePortMonitor: build.mutation<IPortMonitor, Partial<IPortMonitorInput> & {uuid: string}>({
            query: ({uuid, ...data}) => ({
                path: ApiUrls.PORT_MONITOR(uuid),
                method: 'PUT',
                data,
            }),
            transformErrorResponse: withViolations,
            invalidatesTags: (result, error, {uuid}) => [
                { type: 'PortMonitor', id: uuid },
                { type: 'PortMonitor', id: 'LIST' },
                'SummaryPortMonitors',
            ],
        }),
        deletePortMonitor: build.mutation<void, string>({
            query: (uuid) => ({
                path: ApiUrls.PORT_MONITOR(uuid),
                method: 'DELETE',
            }),
            invalidatesTags: () => [{ type: 'PortMonitor', id: 'LIST' }, 'SummaryPortMonitors'],
        }),
        getSummaryPortMonitors: build.query<ISummaryPortMonitors, void>({
            query: () => ({
                path: ApiUrls.SUMMARY_PORT_MONITORS,
            }),
            providesTags: ['SummaryPortMonitors'],
        }),
        getAllPortMonitorIds: build.query<string[], {sort?: IOrderBy, filters?: string[]}>({
            query: ({sort, filters}) => ({
                path: ApiUrls.PORT_MONITORS_IDS(sort, filters),
                headers: {Accept: 'application/json'} as unknown as AxiosRequestHeaders,
            }),
        }),
        startPortMonitor: build.mutation<IPortMonitor, string>({
            query: (uuid) => ({
                path: ApiUrls.PORT_MONITOR_START(uuid),
                method: 'PUT',
            }),
            transformErrorResponse: withViolations,
            invalidatesTags: (result, error, uuid) => [
                { type: 'PortMonitor', id: uuid },
                { type: 'PortMonitor', id: 'LIST' },
                'SummaryPortMonitors',
            ],
        }),
        stopPortMonitor: build.mutation<IPortMonitor, string>({
            query: (uuid) => ({
                path: ApiUrls.PORT_MONITOR_STOP(uuid),
                method: 'PUT',
            }),
            invalidatesTags: (result, error, uuid) => [
                { type: 'PortMonitor', id: uuid },
                { type: 'PortMonitor', id: 'LIST' },
                'SummaryPortMonitors',
            ],
        }),
        checkPortMonitorNow: build.mutation<{status: string}, string>({
            query: (uuid) => ({
                path: ApiUrls.PORT_MONITOR_CHECK_NOW(uuid),
                method: 'GET',
                silent: true,
            }),
            invalidatesTags: (result, error, uuid) => [
                { type: 'PortMonitor', id: uuid },
                { type: 'PortMonitor', id: 'LIST' },
            ],
        }),
    }),
});
